import React, { useState, useEffect } from 'react';

const InstallPrompt = ({ studentName }) => {
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showAndroidPrompt, setShowAndroidPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const appName = studentName ? `${studentName.split(' ')[0]}'s Wallet` : 'My Wallet';
  const dismissKey = `pwa-install-dismissed-${studentName || 'default'}`;

  useEffect(() => {
    // Already installed or dismissed
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem(dismissKey)) return;

    // iOS detection
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) {
      // Delay prompt until user has had time to explore
      const timer = setTimeout(() => setShowIOSPrompt(true), 8000);
      return () => clearTimeout(timer);
    }

    // Android / Chrome - capture install event
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowAndroidPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [dismissKey]);

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, '1');
    setShowIOSPrompt(false);
    setShowAndroidPrompt(false);
    setDismissed(true);
  };

  const handleAndroidInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(dismissKey, '1');
    }
    setDeferredPrompt(null);
    setShowAndroidPrompt(false);
  };

  if (dismissed) return null;

  // Android prompt
  if (showAndroidPrompt) {
    return (
      <div style={styles.overlay}>
        <div style={styles.sheet}>
          <div style={styles.appRow}>
            <div style={styles.appIcon}>W</div>
            <div>
              <div style={styles.appName}>{appName}</div>
              <div style={styles.appSub}>Add to your home screen</div>
            </div>
          </div>
          <p style={styles.desc}>
            Install the app for quick access to your wallet anytime — no browser needed.
          </p>
          <button className="btn btn--primary btn--full" onClick={handleAndroidInstall}>
            Add to Home Screen
          </button>
          <button className="btn btn--ghost btn--full" onClick={handleDismiss}
            style={{ marginTop: 8 }}>
            Not now
          </button>
        </div>
      </div>
    );
  }

  // iOS prompt
  if (showIOSPrompt) {
    return (
      <div style={styles.overlay}>
        <div style={styles.sheet}>
          <div style={styles.appRow}>
            <div style={styles.appIcon}>W</div>
            <div>
              <div style={styles.appName}>{appName}</div>
              <div style={styles.appSub}>Add to your home screen</div>
            </div>
          </div>
          <p style={styles.desc}>
            Install the app so it's always one tap away on your home screen.
          </p>

          <div style={styles.steps}>
            <div style={styles.step}>
              <span style={styles.stepNum}>1</span>
              <span>Tap the <strong>Share</strong> button
                <span style={styles.shareIcon}>⬆</span> at the bottom of Safari
              </span>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNum}>2</span>
              <span>Scroll down and tap <strong>"Add to Home Screen"</strong></span>
            </div>
            <div style={styles.step}>
              <span style={styles.stepNum}>3</span>
              <span>Tap <strong>Add</strong> — done!</span>
            </div>
          </div>

          {/* Visual arrow pointing to share button */}
          <div style={styles.arrowHint}>
            <span>↓ tap the share icon down there</span>
          </div>

          <button className="btn btn--ghost btn--full" onClick={handleDismiss}>
            Got it, I'll do it later
          </button>
        </div>
      </div>
    );
  }

  return null;
};

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-end',
    animation: 'fadeIn 200ms ease',
  },
  sheet: {
    background: 'var(--color-surface)',
    borderRadius: '20px 20px 0 0',
    padding: '24px 24px 40px',
    width: '100%',
    maxWidth: 'var(--max-width)',
    margin: '0 auto',
    boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
  },
  appRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 16,
  },
  appIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    background: '#2D6A4F',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    fontFamily: 'Georgia, serif',
    fontWeight: 700,
    flexShrink: 0,
  },
  appName: {
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    fontSize: '1.1rem',
  },
  appSub: {
    fontSize: '0.875rem',
    color: 'var(--color-text-3)',
    marginTop: 2,
  },
  desc: {
    fontSize: '0.9375rem',
    color: 'var(--color-text-2)',
    lineHeight: 1.5,
    marginBottom: 20,
  },
  steps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 20,
    padding: '16px',
    background: 'var(--color-surface-2)',
    borderRadius: 12,
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    fontSize: '0.9rem',
    lineHeight: 1.4,
  },
  stepNum: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    background: 'var(--color-primary)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.75rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  shareIcon: {
    display: 'inline-block',
    marginLeft: 4,
    padding: '1px 6px',
    background: 'var(--color-border)',
    borderRadius: 4,
    fontSize: '0.875rem',
  },
  arrowHint: {
    textAlign: 'center',
    fontSize: '0.8125rem',
    color: 'var(--color-primary)',
    fontWeight: 600,
    marginBottom: 16,
  },
};

export default InstallPrompt;
